import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Loader2, Navigation } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface LocationFallbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Fetch Mapbox token from edge function (same as map page)
async function getMapboxToken(): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke('get-mapbox-token');
    if (error) throw error;
    return data?.token || null;
  } catch (error) {
    console.error('Error fetching Mapbox token:', error);
    return null;
  }
}

// Simple geocoding using Mapbox
async function geocodeAddress(query: string, token: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
      `country=ca&limit=1&access_token=${token}`
    );
    
    if (!response.ok) {
      throw new Error('Geocoding failed');
    }
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      return { lat, lng };
    }
    
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

export function LocationFallbackDialog({ open, onOpenChange }: LocationFallbackDialogProps) {
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRetryingGeo, setIsRetryingGeo] = useState(false);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);

  // Fetch Mapbox token when dialog opens
  useEffect(() => {
    if (open && !mapboxToken) {
      getMapboxToken().then(setMapboxToken);
    }
  }, [open, mapboxToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!location.trim()) {
      toast.error('Please enter an address or postal code');
      return;
    }

    if (!mapboxToken) {
      toast.error('Map service is unavailable. Please try again.');
      return;
    }

    setIsLoading(true);
    
    const coords = await geocodeAddress(location.trim(), mapboxToken);
    
    if (coords) {
      toast.success('Location found!');
      onOpenChange(false);
      navigate(`/search?lat=${coords.lat}&lng=${coords.lng}&nearby=true`);
    } else {
      toast.error('Could not find that location. Try a different address or postal code.');
    }
    
    setIsLoading(false);
  };

  const handleRetryGeolocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsRetryingGeo(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsRetryingGeo(false);
        toast.success('Location found!');
        onOpenChange(false);
        navigate(`/search?lat=${position.coords.latitude}&lng=${position.coords.longitude}&nearby=true`);
      },
      (error) => {
        setIsRetryingGeo(false);
        let message = 'Location access denied.';
        
        if (error.code === error.PERMISSION_DENIED) {
          message = 'Location permission denied. Please enable it in your browser settings, or enter your address below.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          message = 'Location unavailable. Please enter your address below.';
        } else if (error.code === error.TIMEOUT) {
          message = 'Location request timed out. Please try again or enter your address below.';
        }
        
        toast.error(message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Find Properties Near You
          </DialogTitle>
          <DialogDescription>
            Enter your address or postal code to find nearby properties for sale.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Retry geolocation button */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleRetryGeolocation}
            disabled={isRetryingGeo}
          >
            {isRetryingGeo ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Getting location...
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-2" />
                Use My Current Location
              </>
            )}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or enter manually
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="location">Address or Postal Code</Label>
              <Input
                id="location"
                placeholder="e.g., T5J 0N3 or 10123 Jasper Ave, Edmonton"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Enter a Canadian postal code or street address
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading || !location.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Finding location...
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 mr-2" />
                  Search Nearby Properties
                </>
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
